/* ==========================================================================
   Solar investment calculator — Solar ATAP rules
   --------------------------------------------------------------------------
   Inverts a TNB bill to kWh numerically, then computes the saving by
   re-running the full tariff on the reduced consumption. That is what catches
   the efficiency-rebate band jumps, which is where the real money hides.
   ========================================================================== */

(function () {
  'use strict';

  var DAYS = 30.44;

  /* Energy Efficiency Incentive: a rebate in sen/kWh applied to EVERY unit,
     banded on the month's total, cliffing to zero above 1000 kWh. */
  var EEI = [[200,25],[250,24.5],[300,22.5],[350,21],[400,17],[450,14.5],[500,12],
             [550,10.5],[600,9],[650,7.5],[700,5.5],[750,4.5],[800,4],[850,2.5],[900,1],[1000,0.5]];

  /* Calibrated against an hourly tropical generation curve run against two
     load shapes. match = how much generation lands on a live load;
     share = the ceiling set by how much load exists in daylight. */
  var OCC = {
    empty: { match: 0.45, share: 0.18 },
    home:  { match: 0.80, share: 0.45 },
    biz:   { match: 0.85, share: 0.60 }
  };

  /* The result panel carries its own layout class from the section markup
     (ef-calc__sticky: the padding and the sticky offset). Assigning className
     wholesale wiped it on the first render, so the figures ended up flush
     against the panel edge and the panel stopped sticking. Toggle only the
     reveal classes and leave whatever the markup brought with it. */
  function reveal(el, on) {
    el.classList.toggle('ef-reveal', on);
    el.classList.toggle('is-in', on);
  }

  function money(n) { return 'RM ' + Math.round(n).toLocaleString('en-MY'); }
  function num(n)   { return Math.round(n).toLocaleString('en-MY'); }

  function build(root) {
    if (root.dataset.efSolarBound) return;
    root.dataset.efSolarBound = '1';

    var T = { eLo:0.2703, eHi:0.3703, cap:0.0455, net:0.1285, retail:10, afa:0.038,
              kwtbb:0.016, sst:0.08, exportRate:0.2703, pvout:3.61, costKwp:3800,
              cap1p:5, cap3p:15, tenure:10 };
    try { T = Object.assign(T, JSON.parse(root.querySelector('[data-ef-solar-config]').textContent)); } catch (e) {}

    var occ = 'empty', bill = 400, phase = 1;
    var billEl  = root.querySelector('[data-ef-bill]');
    var billVal = root.querySelector('[data-ef-bill-val]');
    var breakEl = root.querySelector('[data-ef-break]');
    var out     = root.querySelector('[data-ef-result]');

    function eei(k) {
      for (var i = 0; i < EEI.length; i++) if (k <= EEI[i][0]) return EEI[i][1] / 100;
      return 0;
    }

    function billFor(k) {
      if (k <= 0) return 0;
      var energy = k <= 1500 ? k * T.eLo : 1500 * T.eLo + (k - 1500) * T.eHi;
      var net = energy + k * T.cap + k * T.net
              + (k > 600 ? T.retail : 0) + (k > 600 ? k * T.afa : 0) - k * eei(k);
      if (net < 0) net = 0;
      var total = net + (k > 300 ? net * T.kwtbb : 0);
      if (k > 600) {
        var rate = (k <= 1500 ? T.eLo : T.eHi) + T.cap + T.net + T.afa;
        total += (k - 600) * rate * T.sst;
      }
      return total;
    }

    /* Bisection — the rebate cliffs make closed-form inversion fragile. */
    function kwhFor(rm) {
      var lo = 0, hi = 4000;
      for (var i = 0; i < 60; i++) {
        var mid = (lo + hi) / 2;
        if (billFor(mid) < rm) lo = mid; else hi = mid;
      }
      return (lo + hi) / 2;
    }

    function evaluate(kwp, kwh, baseBill) {
      var gen = kwp * T.pvout * DAYS;
      var o = OCC[occ];
      var selfUsed = Math.min(gen * o.match, o.share * kwh, kwh);
      var exported = gen - selfUsed;
      var residual = Math.max(0, kwh - selfUsed);
      var credited = Math.min(exported, residual);   /* same billing period only */
      var forfeited = exported - credited;           /* no rollover, no payout */
      var saveSelf = baseBill - billFor(residual);
      var saveExport = credited * T.exportRate;
      var save = saveSelf + saveExport;
      var cost = kwp * T.costKwp;
      return { kwp:kwp, gen:gen, selfUsed:selfUsed, exported:exported, credited:credited,
               forfeited:forfeited, saveSelf:saveSelf, saveExport:saveExport, save:save,
               cost:cost, payback: save > 0 ? cost / (save * 12) : Infinity,
               newBill: Math.max(0, baseBill - save) };
    }

    /* The largest array that still pays back inside its ATAP tenure. One rule,
       no thresholds to tune, and monotonic: as consumption rises the viable
       set only grows. In practice the 5 kW single-phase cap binds first for
       most Malaysian homes — which is the real finding. */
    function model() {
      var kwh = kwhFor(bill);
      var limit = phase === 1 ? T.cap1p : T.cap3p;
      var all = [];
      for (var k = 1.0; k <= limit + 0.001; k += 0.5) {
        all.push(evaluate(Math.round(k * 10) / 10, kwh, bill));
      }
      /* Two filters, both necessary. Payback keeps it inside the contract.
         The forfeiture cap stops the sizer recommending a roof that generates
         into a void — under ATAP that surplus is worth nothing at all, and a
         payback that still clears ten years can hide a lot of waste. */
      var WASTE = 0.10;
      var best = null;
      for (var i = 0; i < all.length; i++) {
        var r = all[i];
        if (r.payback > T.tenure) continue;
        if (r.gen > 0 && r.forfeited / r.gen > WASTE) continue;
        if (!best || r.kwp > best.kwp) best = r;
      }
      if (!best) for (var j = 0; j < all.length; j++) if (!best || all[j].payback < best.payback) best = all[j];
      best.kwh = kwh;
      best.eff = kwh > 0 ? bill / kwh * 100 : 0;
      best.panels = Math.round(best.kwp * 1000 / 580);
      best.maxed = best.kwp >= limit - 0.001;
      return best;
    }

    function bar(label, value, max, neg) {
      return '<div class="ef-sbar"><span class="ef-sbar__k">' + label + '</span>' +
             '<div class="ef-sbar__track"><div class="ef-sbar__fill' + (neg ? ' is-neg' : '') +
             '" style="width:' + Math.max(0, Math.min(100, value / max * 100)) + '%"></div></div>' +
             '<span class="ef-sbar__v">' + (neg ? '&minus;' : '') + money(Math.abs(value)) + '</span></div>';
    }

    function srow(k, v) {
      return '<div class="ef-res__check"><i>&#183;</i><span class="k">' + k +
             '</span><span class="v">' + v + '</span></div>';
    }

    function render() {
      var m = model();
      billVal.textContent = money(bill);

      /* ---- where the bill goes ---- */
      var k = m.kwh;
      var parts = [
        ['Energy',   k <= 1500 ? k * T.eLo : 1500 * T.eLo + (k - 1500) * T.eHi],
        ['Network',  k * T.net],
        ['Capacity', k * T.cap],
        ['Fuel adjustment', k > 600 ? k * T.afa : 0],
        ['Retail',   k > 600 ? T.retail : 0],
        ['Efficiency rebate', -k * eei(k)]
      ].filter(function (p) { return Math.abs(p[1]) > 0.005; });
      var max = 0;
      parts.forEach(function (p) { if (Math.abs(p[1]) > max) max = Math.abs(p[1]); });

      var bh = '<div class="ef-calc__grp-h" style="margin-top:var(--ef-space-5)">Where that bill goes — ' +
               num(k) + ' kWh at ' + m.eff.toFixed(1) + ' sen effective</div><div class="ef-sbars">';
      parts.forEach(function (p) { bh += bar(p[0], p[1], max, p[1] < 0); });
      bh += '</div>';

      if (k > 940 && k < 1060) {
        bh += '<div class="ef-res__note is-warn">You are sitting on the <b>1,000 kWh rebate cliff</b>. The efficiency rebate applies to every unit and vanishes the moment you cross it, so the last few kilowatt-hours here cost over RM 1.50 each.</div>';
      }

      bh += '<div class="ef-calc__grp-h" style="margin-top:var(--ef-space-5)">What a kilowatt-hour is worth to you</div><div class="ef-sbars">' +
            '<div class="ef-sbar"><span class="ef-sbar__k">Self-consumed</span><div class="ef-sbar__track"><div class="ef-sbar__fill" style="width:100%"></div></div><span class="ef-sbar__v">' + m.eff.toFixed(0) + ' sen</span></div>' +
            '<div class="ef-sbar"><span class="ef-sbar__k">Exported</span><div class="ef-sbar__track"><div class="ef-sbar__fill is-neg" style="width:' + (T.exportRate * 100 / Math.max(m.eff, 1) * 100) + '%"></div></div><span class="ef-sbar__v">' + Math.round(T.exportRate * 100) + ' sen</span></div>' +
            '<div class="ef-sbar"><span class="ef-sbar__k">Surplus beyond your bill</span><div class="ef-sbar__track"><div class="ef-sbar__fill" style="width:0%"></div></div><span class="ef-sbar__v">0 sen</span></div></div>';
      breakEl.innerHTML = bh;

      /* ---- recommendation ---- */
      var h = '<div class="ef-res__lab">Recommended system</div>' +
        '<div class="ef-res__name">' + m.kwp.toFixed(1) + ' kWp<span class="ef-res__sub">about ' +
        m.panels + ' panels &middot; ' + (phase === 1 ? 'single-phase' : 'three-phase') + '</span></div>' +
        '<div><span class="ef-price" style="font-size:26px">' + money(m.cost) + '</span></div>' +
        '<p class="ef-res__run">Generates <b>' + num(m.gen) + ' kWh</b> a month, of which <b>' +
        num(m.selfUsed) + ' kWh</b> lands on a live load.</p>';

      h += '<div class="ef-res__checks">' +
           srow('Saved by self-consuming', money(m.saveSelf) + '/mo') +
           srow('Export credit', money(m.saveExport) + '/mo') +
           srow('New bill', money(m.newBill)) +
           '</div>';

      h += '<div class="ef-res__add">' +
           '<div class="ef-res__row"><span>Total monthly saving</span><b>' + money(m.save) + '</b></div>' +
           '<div class="ef-res__row"><span>Simple payback</span><b>' +
             (isFinite(m.payback) ? m.payback.toFixed(1) + ' years' : '—') + '</b></div>' +
           (m.forfeited > 1 ? '<div class="ef-res__row"><span>Generated but forfeited</span><b style="color:var(--ef-critical)">' + num(m.forfeited) + ' kWh</b></div>' : '') +
           '</div>';

      if (m.maxed) {
        h += '<div class="ef-res__note is-warn">You are at the <b>' + (phase === 1 ? '5 kW single-phase' : '15 kW three-phase') +
             ' ceiling</b> under Solar ATAP clause 8.1 — a regulatory limit, not a roof or budget one.' +
             (phase === 1 ? ' For most Malaysian homes this means the size is close to decided for you, and what actually varies is the payback.' : '') + '</div>';
      } else {
        h += '<div class="ef-res__note is-good">The largest array that still pays back inside its ten-year ATAP tenure. Going bigger generates more but earns less per ringgit, because anything past your own monthly bill is forfeited.</div>';
      }

      h += '<div class="ef-res__note is-crit"><b>Already on NEM?</b> Do not switch. Joining ATAP requires terminating an existing NEM contract, and NEM credited exports one-for-one.</div>';

      h += '<div class="ef-res__alt"><b>On adding a battery:</b> ' +
           (OCC[occ].share < 0.30
             ? 'your house is empty when the sun is out, so most generation is exported at ' + Math.round(T.exportRate * 100) +
               ' sen or forfeited outright. Storing it instead is the only way to capture the full ' + m.eff.toFixed(0) + ' sen.'
             : 'you already consume most of what you generate during the day, so a battery buys resilience rather than savings.') +
           '</div>';

      reveal(out, true);
      out.innerHTML = h;
    }

    billEl.addEventListener('input', function () { bill = +billEl.value; render(); });

    root.querySelectorAll('.ef-calc__scen-btn').forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.dataset.phase) {
          phase = +b.dataset.phase;
          root.querySelectorAll('[data-phase]').forEach(function (o) {
            o.setAttribute('aria-pressed', o === b ? 'true' : 'false');
          });
        } else {
          occ = b.dataset.occ;
          root.querySelectorAll('[data-occ]').forEach(function (o) {
            o.setAttribute('aria-pressed', o === b ? 'true' : 'false');
          });
        }
        render();
      });
    });

    render();
  }

  function init(scope) {
    (scope || document).querySelectorAll('.ef-calc[id^="solar-"]').forEach(build);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else { init(); }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
